"use client";
import { useCallback, useEffect, useState } from "react"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"
import { Fragment } from "@tiptap/pm/model"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Lib ---
import {
  FOCUS_NO_SCROLL,
  findConvertibleAncestor,
  findNodeAroundSelection,
  findNodePosition,
  getSelectedBlockNodes,
  isNodeInSchema,
  isNodeTypeSelected,
  isValidPosition,
  runWithPreservedWindowScroll,
  selectionWithinConvertibleTypes,
} from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { CodeBlockIcon } from "@/tiptap/icons/code-block-icon"

export const CODE_BLOCK_SHORTCUT_KEY = "mod+alt+c"
export const DEFAULT_CODE_BLOCK_LANGUAGE = "plaintext"

const LIST_ITEM_TYPES = new Set(["listItem", "taskItem"])

/**
 * @returns {{ depth: number, node: import("@tiptap/pm/model").Node, pos: number, indexInList: number, listDepth: number } | null}
 */
function findListItemContext($pos) {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth)
    if (LIST_ITEM_TYPES.has(node.type.name)) {
      return {
        depth,
        node,
        pos: $pos.before(depth),
        indexInList: $pos.index(depth - 1),
        listDepth: depth - 1,
      }
    }
  }

  return null
}

// Leaf nodes render as an empty string unless textBetween is told otherwise,
// which drops shift+enter breaks and inline nodes such as emoji.
function leafTextForCodeBlock(leafNode) {
  if (leafNode.type.name === "hardBreak") return "\n"
  if (leafNode.type.spec.toText) return leafNode.type.spec.toText({ node: leafNode })

  return ""
}

function fragmentTextForCodeBlock(fragment) {
  return fragment.textBetween(0, fragment.size, "\n", leafTextForCodeBlock)
}

function nodeTextForCodeBlock(node) {
  return node.textBetween(0, node.content.size, "\n", leafTextForCodeBlock)
}

function createCodeBlock(schema, text, attributes = {}) {
  return schema.nodes.codeBlock.create(
    { language: DEFAULT_CODE_BLOCK_LANGUAGE, ...attributes },
    text ? schema.text(text) : undefined,
  )
}

function createParagraph(schema, text) {
  return schema.nodes.paragraph.create(
    null,
    text ? schema.text(text) : undefined,
  )
}

function createParagraphFromContent(schema, content) {
  if (!content || content.size === 0) {
    return createParagraph(schema)
  }

  return schema.nodes.paragraph.create(null, content)
}

function copyNode(node) {
  return node.copy(node.content)
}

/**
 * Keep numbered/bulleted lists intact when turning content into a code block.
 *
 * TipTap's setCodeBlock → setNode falls back to clearNodes() when a list item's
 * required leading paragraph can't become a code block, which lifts the block
 * out of the list and restarts numbering. Handle that case in-list instead.
 */
function resolveListTextblockSelection(state, selection) {
  let { $from, from, to, empty } = selection

  // NodeSelection on a paragraph (common from block/drag handles) leaves
  // $from.parent as the listItem, which is not a textblock. Treat that as a
  // full selection of the paragraph content so we stay on the list-preserving
  // path instead of falling through to setCodeBlock → clearNodes().
  if (!$from.parent.isTextblock && selection instanceof NodeSelection) {
    const node = selection.node
    if (node?.isTextblock) {
      from = selection.from + 1
      to = selection.to - 1
      empty = from >= to
      $from = state.doc.resolve(Math.min(from, state.doc.content.size))
    } else if (LIST_ITEM_TYPES.has(node?.type.name) && node.firstChild?.isTextblock) {
      const paragraph = node.firstChild
      from = selection.from + 2
      to = selection.from + 1 + paragraph.nodeSize - 1
      empty = from >= to
      $from = state.doc.resolve(Math.min(from, state.doc.content.size))
    }
  }

  return { $from, from, to, empty }
}

function isEffectivelyEmptyInline(content) {
  if (!content || content.size === 0) return true

  return fragmentTextForCodeBlock(content).trim() === ""
}

export function setCodeBlockPreservingList(editor, attributes = {}) {
  if (!editor || !editor.isEditable) return false

  const { state } = editor
  const { selection, schema } = state
  const { $from, from, to, empty } = resolveListTextblockSelection(state, selection)

  if (!$from.parent.isTextblock) {
    return editor
      .chain()
      .focus(undefined, FOCUS_NO_SCROLL)
      .setCodeBlock({ language: DEFAULT_CODE_BLOCK_LANGUAGE, ...attributes })
      .run()
  }

  const listCtx = findListItemContext($from)
  if (!listCtx) {
    return editor
      .chain()
      .focus(undefined, FOCUS_NO_SCROLL)
      .setCodeBlock({ language: DEFAULT_CODE_BLOCK_LANGUAGE, ...attributes })
      .run()
  }

  const textblock = $from.parent
  const textblockPos = $from.before($from.depth)
  const indexInListItem = $from.index(listCtx.depth)
  const codeAttrs = { language: DEFAULT_CODE_BLOCK_LANGUAGE, ...attributes }

  // Non-leading blocks inside a list item can become code blocks in place.
  if (indexInListItem > 0) {
    return editor
      .chain()
      .focus(undefined, FOCUS_NO_SCROLL)
      .setCodeBlock(codeAttrs)
      .run()
  }

  // Empty caret on the list item's leading paragraph: insert a nested code
  // block under the step text (the old "shift+enter then insert code" flow).
  if (empty) {
    const insertPos = textblockPos + textblock.nodeSize
    const codeBlock = createCodeBlock(schema, null, codeAttrs)
    const tr = state.tr.insert(insertPos, codeBlock)
    tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 1)))
    editor.view.dispatch(tr)
    return true
  }

  // Only transform within the current leading paragraph. Cross-item selections
  // would otherwise duplicate later text into the code block.
  const textStart = textblockPos + 1
  const textEnd = textblockPos + textblock.nodeSize - 1
  const clampedFrom = Math.max(from, textStart)
  const clampedTo = Math.min(to, textEnd)
  const relativeFrom = clampedFrom - textStart
  const relativeTo = clampedTo - textStart
  const beforeContent = textblock.content.cut(0, relativeFrom)
  const selectedContent = textblock.content.cut(relativeFrom, relativeTo)
  const afterContent = textblock.content.cut(relativeTo, textblock.content.size)
  const selectedText = fragmentTextForCodeBlock(selectedContent)

  // Keep this list item's number. When the whole leading paragraph becomes
  // code, use the code block as the first child (schema allows paragraph |
  // codeBlock) so we do not leave an empty <p> that Safari drops markers for.
  const children = []
  if (!isEffectivelyEmptyInline(beforeContent)) {
    children.push(createParagraphFromContent(schema, beforeContent))
  }
  children.push(createCodeBlock(schema, selectedText, codeAttrs))
  if (!isEffectivelyEmptyInline(afterContent)) {
    children.push(createParagraphFromContent(schema, afterContent))
  }

  listCtx.node.forEach((child, _offset, index) => {
    if (index > 0) {
      children.push(copyNode(child))
    }
  })

  const newListItem = listCtx.node.type.create(
    listCtx.node.attrs,
    Fragment.from(children),
  )
  const tr = state.tr.replaceWith(
    listCtx.pos,
    listCtx.pos + listCtx.node.nodeSize,
    newListItem,
  )
  const codeIndex = children.findIndex((child) => child.type.name === "codeBlock")
  const codePos =
    listCtx.pos +
    1 +
    children.slice(0, codeIndex).reduce((sum, child) => sum + child.nodeSize, 0)
  tr.setSelection(TextSelection.near(tr.doc.resolve(codePos + 1)))
  editor.view.dispatch(tr)
  return true
}

// Callouts can contain multiple paragraphs/lists. Converting each child with
// setCodeBlock would create one code block per line — collapse to a single
// code block with newlines instead (inverse of codeBlock → callout).
function replaceCalloutWithCodeBlock(editor) {
  const callout = findNodeAroundSelection(editor.state, "callout")
  if (!callout) return false

  const { state } = editor
  const { schema } = state
  const text = nodeTextForCodeBlock(callout.node)
  const codeBlock = createCodeBlock(schema, text)

  editor.view.dispatch(
    state.tr.replaceWith(callout.pos, callout.pos + callout.node.nodeSize, codeBlock),
  )

  return true
}

/**
 * Checks if code block can be toggled in the current editor state
 */
export function canToggle(editor, turnInto = true) {
  if (!editor || !editor.isEditable) return false
  if (
    !isNodeInSchema("codeBlock", editor) ||
    isNodeTypeSelected(editor, ["image"])
  )
    return false

  if (!turnInto) {
    return editor.can().toggleNode("codeBlock", "paragraph");
  }

  // Ensure selection is in nodes we're allowed to convert
  if (
    !selectionWithinConvertibleTypes(editor, [
      "paragraph",
      "heading",
      "bulletList",
      "orderedList",
      "taskList",
      "blockquote",
      "callout",
      "codeBlock",
    ])
  )
    return false

  if (editor.isActive("callout") && findNodeAroundSelection(editor.state, "callout")) {
    return true
  }

  if (findListItemContext(editor.state.selection.$from)) {
    return true
  }

  // Either we can toggle code block directly on the selection,
  // or we can clear formatting/nodes to arrive at a code block.
  return (editor.can().toggleNode("codeBlock", "paragraph") || editor.can().clearNodes());
}

/**
 * Toggles code block in the editor
 */
export function toggleCodeBlock(editor) {
  if (!editor || !editor.isEditable) return false
  if (!canToggle(editor)) return false

  return runWithPreservedWindowScroll(() => {
    try {
      if (editor.isActive("callout")) {
        return replaceCalloutWithCodeBlock(editor)
      }

      if (editor.isActive("codeBlock")) {
        return editor
          .chain()
          .focus(undefined, FOCUS_NO_SCROLL)
          .setNode("paragraph")
          .run()
      }

      const listCtx = findListItemContext(editor.state.selection.$from)
      if (listCtx) {
        const success = setCodeBlockPreservingList(editor)
        if (success) {
          editor.chain().focus(undefined, FOCUS_NO_SCROLL).selectTextblockEnd().run()
        }
        return success
      }

      const view = editor.view
      let state = view.state
      let tr = state.tr

      const blocks = getSelectedBlockNodes(editor)

      // In case a selection contains multiple blocks, we only allow
      // toggling to node if there's exactly one block selected
      // we also dont block the canToggle since it will fall back to the bottom logic
      const isPossibleToTurnInto =
        selectionWithinConvertibleTypes(editor, [
          "paragraph",
          "heading",
          "bulletList",
          "orderedList",
          "taskList",
          "blockquote",
          "callout",
          "codeBlock",
        ]) && blocks.length === 1

      // No selection, find the the cursor position
      if (
        (state.selection.empty || state.selection instanceof TextSelection) &&
        isPossibleToTurnInto
      ) {
        const pos = findNodePosition({
          editor,
          node: findConvertibleAncestor(state.selection),
        })?.pos
        if (!isValidPosition(pos)) return false

        tr = tr.setSelection(NodeSelection.create(state.doc, pos))
        view.dispatch(tr)
        state = view.state
      }

      const selection = state.selection

      let chain = editor.chain().focus(undefined, FOCUS_NO_SCROLL)

      // Handle NodeSelection
      if (selection instanceof NodeSelection) {
        const firstChild = selection.node.firstChild?.firstChild
        const lastChild = selection.node.lastChild?.lastChild

        const from = firstChild
          ? selection.from + firstChild.nodeSize
          : selection.from + 1

        const to = lastChild
          ? selection.to - lastChild.nodeSize
          : selection.to - 1

        const resolvedFrom = state.doc.resolve(from)
        const resolvedTo = state.doc.resolve(to)

        chain = chain
          .setTextSelection(TextSelection.between(resolvedFrom, resolvedTo))
          .clearNodes()
      }

      chain.setCodeBlock({ language: DEFAULT_CODE_BLOCK_LANGUAGE }).run()

      editor.chain().focus(undefined, FOCUS_NO_SCROLL).selectTextblockEnd().run()

      return true
    } catch {
      return false
    }
  })
}

/**
 * Determines if the code block button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isNodeInSchema("codeBlock", editor)) return false

  if (!editor.isActive("code")) {
    return canToggle(editor);
  }

  return true
}

/**
 * Custom hook that provides code block functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage - no params needed
 * function MySimpleCodeBlockButton() {
 *   const { isVisible, isActive, handleToggle } = useCodeBlock()
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <button
 *       onClick={handleToggle}
 *       aria-pressed={isActive}
 *     >
 *       Code Block
 *     </button>
 *   )
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedCodeBlockButton() {
 *   const { isVisible, isActive, handleToggle, label } = useCodeBlock({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onToggled: (isActive) => console.log('Code block toggled:', isActive)
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggle}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       Toggle Code Block
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useCodeBlock(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onToggled,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canToggleState = canToggle(editor)
  const isActive = editor?.isActive("codeBlock") || false

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleToggle = useCallback(() => {
    if (!editor) return false

    const success = toggleCodeBlock(editor)
    if (success) {
      onToggled?.()
    }
    return success
  }, [editor, onToggled])

  return {
    isVisible,
    isActive,
    handleToggle,
    canToggle: canToggleState,
    label: "Code Block",
    shortcutKeys: CODE_BLOCK_SHORTCUT_KEY,
    Icon: CodeBlockIcon,
  }
}
